import React, { useState } from 'react';
import { Edit2, Trash2, Check, X, Merge } from 'lucide-react';
import { api } from '../../utils/api';

export default function RetroCard({
  card, phase, isFacilitator, myParticipantId,
  isRevealed, isLocked, selectedForMerge, onSelectForMerge,
  onUpdated, onDeleted
}) {
  const [editing, setEditing] = useState(false);
  const [editContent, setEditContent] = useState(card.content);
  const [loading, setLoading] = useState(false);

  const isOwn = card.participant_id === myParticipantId;
  const sessionComplete = phase === 'complete';
  const canEdit = !sessionComplete && (isOwn || isFacilitator);
  const canDelete = !sessionComplete && (isOwn || isFacilitator);
  const isHidden = !isRevealed && !isOwn;

  const handleSave = async () => {
    if (!editContent.trim()) return;
    setLoading(true);
    try {
      const updated = await api.cards.update(card.id, { content: editContent, participant_id: myParticipantId });
      onUpdated(updated);
      setEditing(false);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this card?')) return;
    try {
      await api.cards.delete(card.id, { participant_id: myParticipantId });
      onDeleted(card.id);
    } catch (e) { console.error(e); }
  };

  const phaseClass = `phase-${phase}`;

  if (isHidden) {
    return (
      <div className={`retro-card hidden-card ${phaseClass}`} style={{ opacity: 0.5 }}>
        <div className="text-xs text-muted" style={{ fontStyle: 'italic' }}>
          ✏️ Someone is writing...
        </div>
      </div>
    );
  }

  return (
    <div
      className={`retro-card ${phaseClass} ${selectedForMerge ? 'selected' : ''} ${card.is_ai_suggested ? 'ai-card' : ''}`}
      style={{ outline: selectedForMerge ? '2px solid var(--accent)' : 'none' }}
    >
      {/* Sticker */}
      {card.sticker && <div style={{ fontSize: '1.4rem', marginBottom: 4 }}>{card.sticker}</div>}

      {/* Content */}
      {editing ? (
        <div>
          <textarea
            className="input"
            value={editContent}
            onChange={e => setEditContent(e.target.value)}
            rows={3}
            style={{ resize: 'vertical', marginBottom: 8 }}
            autoFocus
          />
          <div className="flex gap-2">
            <button className="btn btn-success btn-sm" onClick={handleSave} disabled={loading}><Check size={12} /></button>
            <button className="btn btn-ghost btn-sm" onClick={() => { setEditing(false); setEditContent(card.content); }}><X size={12} /></button>
          </div>
        </div>
      ) : (
        <p className="text-sm" style={{ lineHeight: 1.6, wordBreak: 'break-word' }}>{card.content}</p>
      )}

      {/* Group label */}
      {card.group_label && !editing && (
        <div style={{ marginTop: 6 }}>
          <span className="badge badge-purple" style={{ fontSize: 10 }}>{card.group_label}</span>
        </div>
      )}

      {/* Author */}
      {isRevealed && !editing && (
        <div className="text-xs text-muted mt-2">{card.is_anonymous ? "— Anonymous" : card.author_name ? `— ${card.author_name}` : null}</div>
      )}

      {/* Controls */}
      {!editing && (
        <div className="flex items-center gap-1 mt-2" style={{ flexWrap: 'wrap' }}>
          {canEdit && (
            <button className="btn btn-ghost btn-icon btn-sm" onClick={() => setEditing(true)} title="Edit">
              <Edit2 size={11} />
            </button>
          )}
          {canDelete && (
            <button className="btn btn-ghost btn-icon btn-sm" style={{ color: 'var(--red)' }} onClick={handleDelete} title="Delete">
              <Trash2 size={11} />
            </button>
          )}
          {isFacilitator && isRevealed && (
            <button
              className={`btn btn-icon btn-sm ${selectedForMerge ? 'btn-primary' : 'btn-ghost'}`}
              onClick={() => onSelectForMerge(card.id)}
              title="Select to merge"
            >
              <Merge size={11} />
            </button>
          )}
        </div>
      )}
    </div>
  );
}
